import React, { useEffect, useState, useCallback } from "react";
import { Plus, Globe } from "lucide-react";
import { PageHeader } from "@/components/Layout";
import { StatusBadge, Pipeline, ProgressBar, EmptyState } from "@/components/Bits";
import TaskDrawer from "@/components/TaskDrawer";
import TaskFormDialog from "@/components/TaskFormDialog";
import { DIVISION_PIPELINES } from "@/lib/ui";
import api from "@/lib/api";

export default function Websites() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [team, setTeam] = useState([]);
  const [drawerId, setDrawerId] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const load = useCallback(() => api.get("/tasks", { params: { division: "Website & Digital Build" } }).then((r) => setTasks(r.data)), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/team").then((r) => setTeam(r.data));
  }, []);

  return (
    <div>
      <PageHeader
        title="Website & Digital Build"
        subtitle="Sites, landing pages, QA & launch tracking"
        actions={
          <button data-testid="new-website-button" onClick={() => setFormOpen(true)} className="flex items-center gap-2 text-sm bg-white text-black font-medium px-4 py-2 rounded-sm hover:bg-zinc-200 transition-colors active:scale-[0.98]">
            <Plus size={16} /> New Project
          </button>
        }
      />
      <div className="p-8 space-y-6">
        <div className="bg-[#121212] border border-white/10 rounded-sm p-6">
          <Pipeline steps={DIVISION_PIPELINES["Website & Digital Build"]} testId="website-pipeline" />
        </div>

        {tasks.length === 0 ? (
          <EmptyState icon={Globe} title="No website projects" hint="Kick off your first build." />
        ) : (
          <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-white/10 bg-[#0a0a0a]">
                  {["Brand", "Project", "Domain", "Owner", "Status", "Progress"].map((h) => (
                    <th key={h} className="py-3 px-3 font-mono text-[11px] uppercase tracking-wider text-zinc-500">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tasks.map((t) => (
                  <tr key={t.id} data-testid={`website-row-${t.id}`} onClick={() => { setDrawerId(t.id); setDrawerOpen(true); }} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                    <td className="py-3 px-3 text-zinc-200">{t.brand}</td>
                    <td className="py-3 px-3 text-zinc-300 max-w-[220px] truncate">{t.title}</td>
                    <td className="py-3 px-3 text-zinc-400 font-mono text-xs">{t.domain || "—"}</td>
                    <td className="py-3 px-3 text-zinc-400">{t.assigned_to || "—"}</td>
                    <td className="py-3 px-3"><StatusBadge status={t.status} /></td>
                    <td className="py-3 px-3 w-32">
                      <div className="flex items-center gap-2">
                        <ProgressBar value={t.progress} />
                        <span className="text-xs font-mono text-zinc-500 w-8">{t.progress}%</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <TaskDrawer taskId={drawerId} open={drawerOpen} onOpenChange={setDrawerOpen} onChanged={load} />
      <TaskFormDialog open={formOpen} onOpenChange={setFormOpen} clients={clients} team={team} onCreated={load} defaultDivision="Website & Digital Build" />
    </div>
  );
}
